import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { TERMS_TEXT } from '../lib/termsContent';
import AppFooter from '../components/AppFooter';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a87706c-aea0-440d-a2df-f352ed3952e5/artifacts/qdekaw6t_Screenshot%202024-07-25%20103325.png";
const BG_IMAGE = "https://images.unsplash.com/photo-1758208974170-3a3037da0c69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwYWJzdHJhY3QlMjBibHVlJTIwd2hpdGV8ZW58MHx8fHwxNzY3ODY3NjgzfDA&ixlib=rb-4.1.0&q=85";


export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !businessName || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!acceptedTerms) {
      toast.error('You must accept the Terms and Conditions');
      return;
    }

    setLoading(true);
    try {
      await register(email, password, name, businessName, acceptedTerms);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          <img src={LOGO_URL} alt="Logo" className="w-24 h-24 mb-8 logo-pulse" />
          <h1 className="text-4xl font-bold mb-4 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Cash Flow Timing Planner
          </h1>
          <p className="text-lg text-white/80 text-center max-w-md">
            Start planning your cash flow with precision. Track payments, forecast timing, and stay ahead of your finances.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 overflow-y-auto">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex justify-center mb-4">
              <img src={LOGO_URL} alt="Logo" className="w-16 h-16" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Create Account
            </CardTitle>
            <CardDescription className="text-slate-500">
              Get started with your cash flow planning
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Your Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-11"
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessName" className="text-slate-700">Business Name</Label>
                <Input
                  id="businessName"
                  type="text"
                  placeholder="ABC Pty Ltd"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="h-11"
                  data-testid="register-business-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  data-testid="register-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-11"
                  data-testid="register-confirm-password-input"
                />
              </div>

              {/* Terms and Conditions */}
              <div className="terms-checkbox">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                  data-testid="terms-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer">
                    I have read and accept the{' '}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button 
                          type="button" 
                          className="text-primary font-medium hover:underline"
                          data-testid="terms-link"
                        >
                          Terms of Use, Disclaimer and Privacy Notice
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Terms of Use, Disclaimer and Privacy Notice
                          </DialogTitle>
                        </DialogHeader>
                        <ScrollArea className="h-[60vh] pr-4">
                          <div className="text-sm text-slate-600 whitespace-pre-line leading-relaxed">
                            {TERMS_TEXT}
                          </div>
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Including disclaimer that this tool does not provide financial advice
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-medium"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
      <AppFooter />
    </div>
  );
}
